<div class="roles-dialog">

  <!-- ── Header ─────────────────────────────────────────────────────────── -->
  <div class="roles-dialog__header">
    <div class="roles-dialog__user">
      <div class="roles-dialog__avatar"
        [style.background-color]="getAvatarColor(user.username)">
        {{ getUserInitials(user.firstName, user.lastName) }}
      </div>
      <div class="roles-dialog__user-info">
        <h2 class="roles-dialog__title">Gestion des rôles</h2>
        <p class="roles-dialog__subtitle">
          <span>{{ user.firstName }} {{ user.lastName }}</span>
          <span class="sep">·</span>
          <span class="muted">{{ user.email }}</span>
        </p>
      </div>
    </div>

    <div class="roles-dialog__header-right">
      <!-- Changes badge -->
      @if (hasChanges) {
        <span class="changes-badge">
          {{ pendingChanges.length }} modification(s)
        </span>
      }
      <!-- Reset -->
      @if (hasChanges) {
        <button class="btn-ghost btn-sm" (click)="resetAll()" title="Tout réinitialiser">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M1 4V10H7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M3.51 15C4.15839 16.8399 5.38734 18.4187 7.01166 19.5013C8.63598 20.5839 10.5677 21.1099 12.5157 20.9859C14.4637 20.8619 16.3155 20.0945 17.7853 18.8099C19.2551 17.5252 20.2606 15.7998 20.6472 13.8955C21.0338 11.9911 20.7799 10.0124 19.9240 8.268C19.0681 6.5235 17.6556 5.1082 15.9133 4.2440C14.171 3.3798 12.1961 3.0166 10.2392 3.2103C8.28231 3.4041 6.44458 4.1440 4.99 5.33L1 9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Réinitialiser
        </button>
      }
      <!-- Close -->
      <button class="btn-icon-close" (click)="onCancel()" title="Fermer">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
        </svg>
      </button>
    </div>
  </div>

  <!-- ── Stats bar ────────────────────────────────────────────────────────── -->
  <div class="roles-dialog__stats">
    <div class="stat">
      <span class="stat__value">{{ applications.length }}</span>
      <span class="stat__label">Applications</span>
    </div>
    <div class="stat-divider"></div>
    <div class="stat">
      <span class="stat__value">{{ getTotalRoles() }}</span>
      <span class="stat__label">Rôles disponibles</span>
    </div>
    <div class="stat-divider"></div>
    <div class="stat">
      <span class="stat__value accent">{{ getTotalSelectedRoles() }}</span>
      <span class="stat__label">Rôles attribués</span>
    </div>

    <!-- Search pushed right -->
    <div class="roles-dialog__search">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <circle cx="11" cy="11" r="8" stroke="#8D9495" stroke-width="1.5"/>
        <path d="M21 21L16.65 16.65" stroke="#8D9495" stroke-width="1.5" stroke-linecap="round"/>
      </svg>
      <input
        type="text"
        placeholder="Rechercher une application..."
        [ngModel]="appSearchTerm"
        (ngModelChange)="onSearchChange($event)"
        class="search-input">
        @if (appSearchTerm) {
          <button class="search-clear" (click)="onSearchChange('')">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="#8D9495" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
          </button>
        }
      </div>
    </div>

    <!-- ── Body ─────────────────────────────────────────────────────────────── -->
    <div class="roles-dialog__body">

      <!-- Loader -->
      @if (isLoading) {
        <div class="state-empty">
          <div class="loader-ring"></div>
          <p>Chargement des applications et rôles...</p>
        </div>
      }

      <!-- Empty search -->
      @if (!isLoading && filteredApplications.length === 0 && appSearchTerm) {
        <div class="state-empty">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="8" stroke="#C4C8C8" stroke-width="1.5"/>
            <path d="M21 21L16.65 16.65" stroke="#C4C8C8" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
          <p>Aucune application correspondant à "<strong>{{ appSearchTerm }}</strong>"</p>
        </div>
      }

      <!-- No apps at all -->
      @if (!isLoading && applications.length === 0 && !appSearchTerm) {
        <div class="state-empty">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="3" width="20" height="14" rx="3" stroke="#C4C8C8" stroke-width="1.5"/>
            <path d="M8 21H16M12 17V21" stroke="#C4C8C8" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
          <p>Aucune application disponible</p>
        </div>
      }

      <!-- Applications list -->
      @if (!isLoading && filteredApplications.length > 0) {
        <div class="apps-list">
          @for (app of filteredApplications; track app) {
            <div
              class="app-card"
              [class.app-card--has-roles]="getSelectedRolesCount(app) > 0"
              [class.app-card--expanded]="app.expanded">
              <!-- App header row -->
              <div class="app-card__header" (click)="toggleApp(app)">
                <mat-checkbox
                  [checked]="isAppFullySelected(app)"
                  [indeterminate]="isAppPartiallySelected(app)"
                  (change)="toggleAppSelection(app, $event)"
                  (click)="$event.stopPropagation()"
                  color="primary"
                  class="app-checkbox">
                </mat-checkbox>
                <div class="app-card__icon">
                  @if (app.icon === 'language') {
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/><path d="M2 12H22M12 2C9.33 7 9.33 17 12 22M12 2C14.67 7 14.67 17 12 22" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                  }
                  @if (app.icon === 'smartphone') {
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="5" y="2" width="14" height="20" rx="3" stroke="currentColor" stroke-width="1.5"/><path d="M12 18H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
                  }
                  @if (app.icon === 'api') {
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M7 8L3 12L7 16M17 8L21 12L17 16M14 4L10 20" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  }
                  @if (app.icon === 'admin_panel_settings' || app.icon === 'apps') {
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2L3 7V12C3 16.55 6.84 20.74 12 22C17.16 20.74 21 16.55 21 12V7L12 2Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  }
                </div>
                <div class="app-card__name-wrap">
                  <span class="app-card__name">{{ app.name }}</span>
                  @if (app.description) {
                    <span class="app-card__desc">{{ app.description }}</span>
                  }
                </div>
                <div class="app-card__meta">
                  <span class="roles-count" [class.roles-count--active]="getSelectedRolesCount(app) > 0">
                    {{ getSelectedRolesCount(app) }}<span class="roles-count__total">/{{ app.roles?.length || 0 }}</span>
                  </span>
                </div>
                <div class="app-card__chevron">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
              <!-- Roles panel -->
              @if (app.expanded) {
                <div class="app-card__roles">
                  <div class="roles-panel">
                    <div class="roles-panel__toolbar">
                      <span class="roles-panel__count">{{ app.roles?.length || 0 }} rôle(s) disponible(s)</span>
                      <button class="btn-ghost btn-xs" (click)="selectAllAppRoles(app)">
                        {{ isAppFullySelected(app) ? 'Tout désélectionner' : 'Tout sélectionner' }}
                      </button>
                    </div>
                    @if (!app.roles || app.roles.length === 0) {
                      <div class="roles-empty">
                        Aucun rôle disponible pour cette application.
                      </div>
                    }
                    @if (app.roles && app.roles.length > 0) {
                      <div class="roles-grid">
                        @for (role of app.roles; track role) {
                          <div
                            class="role-chip"
                            [class.role-chip--selected]="isRoleSelected(app, role)">
                            <mat-checkbox
                              [checked]="isRoleSelected(app, role)"
                              (change)="toggleRoleSelection(app, role, $event)"
                              color="primary"
                              class="role-checkbox">
                              <div class="role-chip__content">
                                <span class="role-chip__name">{{ role.title || role.name }}</span>
                                @if (role.description) {
                                  <span class="role-chip__desc">{{ role.description }}</span>
                                }
                              </div>
                            </mat-checkbox>
                          </div>
                        }
                      </div>
                    }
                  </div>
                </div>
              }
            </div>
          }
        </div>
      }
    </div>

    <!-- ── Footer ────────────────────────────────────────────────────────────── -->
    <div class="roles-dialog__footer">
      <button class="btn-secondary" (click)="onCancel()" [disabled]="isSaving">
        Annuler
      </button>
      <button
        class="btn-primary"
        (click)="onSave()"
        [disabled]="!hasChanges || isSaving">
        @if (!isSaving) {
          <span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M19 21H5C3.89 21 3 20.11 3 19V5C3 3.89 3.89 3 5 3H16L21 8V19C21 20.11 20.11 21 19 21Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M17 21V13H7V21M7 3V8H15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Enregistrer{{ hasChanges ? ' (' + pendingChanges.length + ')' : '' }}
          </span>
        }
        @if (isSaving) {
          <span class="saving-state">
            <span class="spinner-sm"></span>
            Enregistrement...
          </span>
        }
      </button>
    </div>

  </div>